import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Toast, useToast } from '@zillow/constellation-10';
import { getCurrentSessionURL } from '../utils/fullStory';
import type { TriggerLocation } from '../constants/clickstreamEvents/globals';
import {
  emitCustomReplyCreatedEvent,
  emitCustomReplyEditedEvent,
  emitDefaultReplyEditedEvent,
} from '../constants/clickstreamEvents/savedReplyModal';
import isDefaultSavedReply from '../utils/isDefaultSavedReply';
import hasDuplicateReplyTitle from '../utils/hasDuplicateReplyTitle';
import { createSavedReplyTextStore } from '../lib/inboxStores';
import { useAuth } from '../AuthProvider';
import {
  useSatelliteWebLandlordSavedReplyHandler_addSavedReplies,
  useSatelliteWebLandlordSavedReplyHandler_deleteSavedReplies,
  useSatelliteWebLandlordSavedReplyHandler_editSavedReply,
  useSatelliteWebLandlordSavedReplyHandler_getSavedReplies,
} from '@zg-rentals/ts-satellite-client-react-query';
import type { CustomReplyDto, DefaultSavedReplyDto } from '@zg-rentals/ts-satellite-client';

type Modes = 'create' | 'edit' | 'view';
const MAX_RETRY_ATTEMPTS = 3;
export const MAX_SAVED_REPLIES_COUNT = 20;

export type SavedReply = CustomReplyDto | DefaultSavedReplyDto;

export const useSavedReplies = ({
  listingAlias,
  leadId,
  triggerLocation,
}: {
  listingAlias: string;
  leadId?: string | null;
  triggerLocation: TriggerLocation;
}) => {
  const { enqueueToast } = useToast();
  const { user } = useAuth();
  const fullStorySessionUrl = getCurrentSessionURL();

  const inboxPageCreateSavedReplyText = createSavedReplyTextStore.useValue();

  const [mode, setMode] = useState<Modes>('view');
  const [hasFormError, setHasFormError] = useState(false);
  const hasInitiallyLoadedRef = useRef(false);
  const shouldSelectFirstReplyRef = useRef(false);

  const [selectedSavedReply, setSelectedSavedReply] = useState<SavedReply>({
    reply: '',
    replyIndex: 0,
    title: '',
  });

  // Tracks changes when a saved reply is being edited
  const [editedSavedReply, setEditedSavedReply] = useState<SavedReply>({
    reply: '',
    replyIndex: 0,
    title: '',
  });

  useEffect(() => {
    if (inboxPageCreateSavedReplyText) {
      setEditedSavedReply({
        reply: inboxPageCreateSavedReplyText,
        replyIndex: 0,
        title: '',
      });
      setMode('create');
    }
  }, [inboxPageCreateSavedReplyText]);

  const {
    data: savedReplyData,
    isLoading: isLoadingSavedReplies,
    error: hasErrorLoadingSavedReplies,
  } = useSatelliteWebLandlordSavedReplyHandler_getSavedReplies({
    retry: MAX_RETRY_ATTEMPTS,
    staleTime: Infinity, // Never auto-refetch - we invalidate manually after mutations
    gcTime: 10 * 60 * 1000, // Keep in cache for 10 minutes after component unmounts
    meta: {
      tags: ['satellite:savedReplies'],
      context: {
        api: 'getSavedReplies',
        details: 'SavedReplyModal - Error fetching saved replies',
        fullStorySessionUrl,
      },
    },
  });

  // Combine custom and default replies into a single array
  const savedReplies = useMemo(() => {
    if (!savedReplyData) return [];
    const customReplies = savedReplyData.customReplies;
    const defaultReplies = savedReplyData.defaultSavedReplies;
    return [...customReplies, ...defaultReplies];
  }, [savedReplyData]);

  // Get custom replies count separately for limit checking
  const customRepliesCount = savedReplyData?.customReplies.length ?? 0;

  // Set the first reply as selected when data loads initially or after creating a new reply
  useEffect(() => {
    if (!hasInitiallyLoadedRef.current && savedReplies.length > 0 && savedReplies[0]) {
      setSelectedSavedReply(savedReplies[0]);
      hasInitiallyLoadedRef.current = true;
    } else if (shouldSelectFirstReplyRef.current && savedReplies.length > 0 && savedReplies[0]) {
      // After creating a new reply, select the first one (backend returns newest custom reply first)
      setSelectedSavedReply(savedReplies[0]);
      shouldSelectFirstReplyRef.current = false;
    }
  }, [savedReplies]);

  // Check if we're trying to add text from MessageToolbar but have reached the limit
  const hasTextFromToolbar = editedSavedReply.reply && mode === 'create';
  const hasReachedMaxCustomRepliesLimit = customRepliesCount >= MAX_SAVED_REPLIES_COUNT;
  const shouldShowLimitError = hasReachedMaxCustomRepliesLimit && hasTextFromToolbar;

  // Check if the current title already exists in saved replies (excluding the current reply being edited)
  const hasDuplicateTitle = useCallback(
    (title: string): boolean => {
      return hasDuplicateReplyTitle({
        title,
        savedReplies,
        excludeReplyIndex: mode === 'edit' ? editedSavedReply.replyIndex : undefined,
      });
    },
    [savedReplies, mode, editedSavedReply.replyIndex],
  );

  // Auto-switch to view mode if limit is reached and user is trying to create
  useEffect(() => {
    if (shouldShowLimitError) {
      setMode('view');
    }
  }, [shouldShowLimitError]);

  const { mutate: addSavedReplies, isPending: isSavingNewReply } =
    useSatelliteWebLandlordSavedReplyHandler_addSavedReplies({
      meta: {
        invalidates: ['satellite:savedReplies'],
        context: {
          api: 'addSavedReplies',
          details: 'SavedReplyModal - Error adding saved replies',
          fullStorySessionUrl,
        },
      },
    });

  const { mutate: deleteSavedReplies, isPending: isDeletingReply } =
    useSatelliteWebLandlordSavedReplyHandler_deleteSavedReplies({
      meta: {
        invalidates: ['satellite:savedReplies'],
        context: {
          api: 'deleteSavedReplies',
          details: 'SavedReplyModal - Error deleting saved reply',
          fullStorySessionUrl,
        },
      },
    });

  const { mutate: editSavedReply, isPending: isSavingEditedReply } =
    useSatelliteWebLandlordSavedReplyHandler_editSavedReply({
      meta: {
        invalidates: ['satellite:savedReplies'],
        context: {
          api: 'editSavedReply',
          details: 'SavedReplyModal - Error editing saved reply',
          fullStorySessionUrl,
        },
      },
    });

  const handleDelete = (replyIndex: number): void => {
    deleteSavedReplies(
      {
        postData: {
          savedReplyIndices: [replyIndex],
        },
      },
      {
        onSuccess() {
          // If the deleted reply was selected, select the first available reply after deletion
          if (selectedSavedReply.replyIndex === replyIndex) {
            const remainingReplies = savedReplies.filter((reply) => reply?.replyIndex !== replyIndex);
            if (remainingReplies.length > 0 && remainingReplies[0]) {
              setSelectedSavedReply(remainingReplies[0]);
            }
          }

          enqueueToast(<Toast tone="success">Saved reply deleted</Toast>);
        },
        onError(_err) {
          enqueueToast(<Toast tone="critical">Saved reply couldn’t be deleted. Refresh and try again.</Toast>);
        },
      },
    );
  };

  const handleEdit = (replyIndex: number): void => {
    setHasFormError(false);
    const foundReply = savedReplies.find((reply) => reply?.replyIndex === replyIndex);
    if (foundReply) {
      setEditedSavedReply(foundReply);
      setMode('edit');
    }
  };

  const handleSelect = (replyIndex: number): void => {
    const foundReply = savedReplies.find((reply) => reply?.replyIndex === replyIndex);
    if (foundReply) {
      setSelectedSavedReply(foundReply);
    }
  };

  const handleCreate = (): void => {
    const emptySavedReply: SavedReply = {
      title: '',
      reply: '',
      replyIndex: 0,
    };

    setEditedSavedReply(emptySavedReply);
    setMode('create');
  };

  const handleSaveAsNewReply = (replyToSave: { title: string; reply: string }): void => {
    addSavedReplies(
      {
        postData: {
          savedReplies: [{ title: replyToSave.title, reply: replyToSave.reply }],
        },
      },
      {
        onSuccess() {
          // Emit appropriate event based on whether we're creating new or editing a default reply
          if (mode === 'edit') {
            emitDefaultReplyEditedEvent({ user, listing: { listingAlias }, triggerLocation, lead: { leadId } });
          } else if (mode === 'create') {
            emitCustomReplyCreatedEvent({ user, listing: { listingAlias }, triggerLocation, lead: { leadId } });
          }

          // Set flag to select the newly created reply (which will be first in the list)
          shouldSelectFirstReplyRef.current = true;

          setMode('view');
          enqueueToast(<Toast tone="success">Saved reply added</Toast>);
        },
        onError(_err) {
          setHasFormError(true);
        },
      },
    );
  };

  const handleSaveAsExistingReply = (replyToSave: { title: string; reply: string; replyIndex: number }): void => {
    editSavedReply(
      {
        postData: {
          title: replyToSave.title,
          reply: replyToSave.reply,
          replyIndex: replyToSave.replyIndex,
        },
      },
      {
        onSuccess() {
          emitCustomReplyEditedEvent({ user, listing: { listingAlias }, triggerLocation, lead: { leadId } });

          // Keep selectedSavedReply in sync so "Choose reply" uses the updated content
          setSelectedSavedReply((prev) => ({
            ...prev,
            title: replyToSave.title,
            reply: replyToSave.reply,
            replyIndex: replyToSave.replyIndex,
          }));
          setMode('view');
          enqueueToast(<Toast tone="success">Saved reply updated</Toast>);
        },
        onError(_err) {
          setHasFormError(true);
        },
      },
    );
  };

  const handleSave = (): void => {
    const { title, reply, replyIndex } = editedSavedReply;
    const replyToSave = {
      title: title.trim(),
      reply: reply.trim(),
      replyIndex: replyIndex,
    };

    // Prevent any updates if there are no changes to save
    if (replyToSave.title === selectedSavedReply.title && replyToSave.reply === selectedSavedReply.reply) {
      return handleCancel();
    }

    createSavedReplyTextStore.set('');

    // Default replies get added as a new reply when edited & saved (they can't be modified in-place)
    if (mode === 'create' || isDefaultSavedReply(replyToSave)) {
      handleSaveAsNewReply(replyToSave);
    } else {
      handleSaveAsExistingReply(replyToSave);
    }
  };

  const handleCancel = (): void => {
    setMode('view');
    setHasFormError(false);
    createSavedReplyTextStore.set('');
  };

  return {
    editedSavedReply,
    handleCancel,
    handleCreate,
    handleDelete,
    handleEdit,
    handleSave,
    handleSelect,
    hasErrorLoadingSavedReplies: !!hasErrorLoadingSavedReplies,
    hasFormError,
    hasReachedMaxCustomRepliesLimit,
    hasDuplicateTitle,
    isDeletingReply,
    isLoadingSavedReplies,
    isSavingReply: isSavingEditedReply || isSavingNewReply,
    mode,
    savedReplies,
    selectedSavedReply,
    setEditedSavedReply,
  };
};
